// 1) Header tabs: a green frame that slides between tabs (follows hover, returns to the current
//    page, and on navigation glides from the previous tab to the new one).
// 2) Product options (capacity buttons, colour swatches): the green selection frame glides from
//    the previous choice to the new one, even though the options are re-rendered on change.
(() => {
  const EASE = 'cubic-bezier(0.83, 0, 0.2, 0.99)';

  /* ---------------- header tabs ---------------- */
  const initTabs = () => {
    const nav = document.querySelector('[data-xo-tabs]');
    if (!nav || nav.__xo) return;
    nav.__xo = true;
    const tabs = [...nav.querySelectorAll(':scope > a')];
    if (!tabs.length) return;
    const ind = document.createElement('span');
    ind.className = 'xo-tab-indicator';
    ind.setAttribute('aria-hidden', 'true');
    nav.prepend(ind);
    const activeIndex = tabs.findIndex((t) => t.classList.contains('xo-tab-active'));

    let current = null;
    // Underline: as wide as the word (tab minus its side padding), sitting just under the text.
    const geo = (tab) => {
      const cs = getComputedStyle(tab);
      const pad = parseFloat(cs.paddingLeft) || 0;
      return { x: tab.offsetLeft + pad, w: tab.offsetWidth - pad * 2, h: 2, y: tab.offsetTop + tab.offsetHeight - 7 };
    };
    const mark = (tab) => tabs.forEach((t) => t.classList.toggle('xo-tab-on', t === tab));
    // While the frame travels, every tab it passes over lights up green (live, frame by frame).
    let tracking = 0;
    const track = (until) => {
      const id = ++tracking;
      const step = () => {
        if (id !== tracking) return;
        const r = ind.getBoundingClientRect();
        tabs.forEach((t) => {
          const b = t.getBoundingClientRect();
          const overlap = Math.min(r.right, b.right) - Math.max(r.left, b.left);
          t.classList.toggle('xo-tab-on', overlap > b.width * 0.35);
        });
        if (performance.now() < until) requestAnimationFrame(step);
        else mark(current);
      };
      requestAnimationFrame(step);
    };
    const place = (tab, animate) => {
      if (!tab) { ind.style.opacity = '0'; mark(null); return; }
      const to = geo(tab);
      ind.style.height = `${to.h}px`;
      ind.style.top = `${to.y}px`;
      ind.style.opacity = '1';
      if (!animate || !current || current === tab) {
        ind.getAnimations().forEach((a) => a.cancel());
        ind.style.width = `${to.w}px`;
        ind.style.transform = `translateX(${to.x}px)`;
        current = tab;
        tracking++;
        mark(tab);
        return;
      }
      // Liquid "merge": the frame first stretches to cover both tabs, then shrinks into the new one.
      const box = ind.getBoundingClientRect();
      const navBox = nav.getBoundingClientRect();
      const fromX = box.left - navBox.left;
      const fromW = box.width;
      const left = Math.min(fromX, to.x);
      const right = Math.max(fromX + fromW, to.x + to.w);
      ind.getAnimations().forEach((a) => a.cancel());
      ind.style.width = `${to.w}px`;
      ind.style.transform = `translateX(${to.x}px)`;
      ind.animate(
        [
          { width: `${fromW}px`, transform: `translateX(${fromX}px)` },
          { width: `${right - left}px`, transform: `translateX(${left}px)`, offset: 0.45 },
          { width: `${to.w}px`, transform: `translateX(${to.x}px)` },
        ],
        { duration: 520, easing: EASE }
      );
      current = tab;
      track(performance.now() + 540);
    };

    let from = parseInt(sessionStorage.getItem('xoTabFrom') ?? '', 10);
    sessionStorage.removeItem('xoTabFrom');
    const active = tabs[activeIndex];
    if (!Number.isNaN(from) && tabs[from] && active && from !== activeIndex) {
      place(tabs[from], false);
      requestAnimationFrame(() => requestAnimationFrame(() => place(active, true)));
    } else {
      place(active, false);
    }

    tabs.forEach((tab, i) => {
      tab.addEventListener('pointerenter', () => place(tab, true));
      tab.addEventListener('focus', () => place(tab, true));
      tab.addEventListener('click', () => sessionStorage.setItem('xoTabFrom', String(activeIndex)));
    });
    nav.addEventListener('pointerleave', () => place(active, true));
    window.addEventListener('resize', () => place(active, false));
  };

  /* ---------------- product options ---------------- */
  // The green frame flies from the old choice to the tapped one immediately (no waiting for the
  // variant to load). It lives in a fixed overlay, so the options re-rendering does not cut it.
  // The frame only flies once the choice really changes (a finger that starts a scroll on an
  // option no longer moves the frame to an option that was never selected).
  let moving = null;
  let pending = null;
  document.addEventListener('pointerdown', (e) => {
    pending = null;
    const label = e.target.closest?.('s-variant-picker label');
    if (!label) return;
    const input = label.querySelector('input') || document.getElementById(label.htmlFor);
    if (!input || input.disabled || input.checked) return;
    const fieldset = label.closest('fieldset');
    const checked = fieldset?.querySelector('input:checked');
    const from = checked?.nextElementSibling;
    if (!from) return;
    pending = { input, a: from.getBoundingClientRect() };
  }, true);
  document.addEventListener('change', (e) => {
    const input = e.target;
    if (!pending || pending.input !== input || !input.checked) { pending = null; return; }
    const a = pending.a;
    pending = null;
    const to = input.nextElementSibling;
    // the pointer/finger corners are already on the new choice: no second, travelling marker
    if (to && (to.hasAttribute('data-xo-snapped') || to.hasAttribute('data-xo-snapped-touch'))) return;
    if (!to) return;
    const b = to.getBoundingClientRect();
    moving?.remove();
    const ind = document.createElement('span');
    ind.className = 'xo-var-indicator';
    ind.setAttribute('aria-hidden', 'true');
    Object.assign(ind.style, { position: 'fixed', left: '0px', top: '0px', width: `${a.width}px`, height: `${a.height}px`, transform: `translate(${a.left}px, ${a.top}px)` });
    document.body.append(ind);
    moving = ind;
    // Hide this option group's own selected border while the frame travels.
    let style = document.getElementById('xo-var-moving');
    if (!style) { style = document.createElement('style'); style.id = 'xo-var-moving'; document.head.append(style); }
    const name = CSS.escape(input.name || '');
    style.textContent = name ? `s-variant-picker input[name="${name}"]:checked + span{border-color:transparent!important;outline-color:transparent!important}s-variant-picker input[name="${name}"]:checked + span::after{opacity:0}` : '';
    ind.animate(
      [
        { width: `${a.width}px`, height: `${a.height}px`, transform: `translate(${a.left}px, ${a.top}px)` },
        { width: `${b.width}px`, height: `${b.height}px`, transform: `translate(${b.left}px, ${b.top}px)` },
      ],
      { duration: 320, easing: EASE, fill: 'forwards' }
    ).finished.then(() => {
      if (moving !== ind) return;
      style.textContent = '';
      ind.remove();
      moving = null;
      document.querySelectorAll(`s-variant-picker input[name="${name}"]:checked + span`).forEach((s) => {
        s.classList.remove('xo-var-landed'); void s.offsetWidth; s.classList.add('xo-var-landed');
      });
    }).catch(() => {}).finally(() => {
      // never leave the chosen colour/option without its corners, even if the move was interrupted
      if (moving === ind || !moving) style.textContent = '';
      if (moving === ind) { ind.remove(); moving = null; }
    });
    // safety net: whatever happens, the corners come back shortly after
    setTimeout(() => { if (moving !== ind) return; style.textContent = ''; ind.remove(); moving = null; }, 900);
  }, true);

  const start = () => {
    initTabs();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

// Touch: four green corners. They appear when the finger rests on / taps an image, an option or a
// brand chip and stay on it (riding along while scrolling, never jumping). On a frameless text link
// (footer, policies) the corners appear on the word, then spread into a line under it.
(() => {
  if (!window.matchMedia('(hover: none), (pointer: coarse)').matches) return;
  // Icons (cart, menu, search, X, social…) get no corners on touch: they just turn green when pressed.
  const isIcon = (n) => { if (!n.querySelector('svg')) return false; const r = n.getBoundingClientRect(); return r.width <= 60 && r.height <= 60; };
  const resolve = (el) => { const info = window.xoSnapTarget ? window.xoSnapTarget(el) : null; if (info && info.el.closest('[data-xo-logo]')) return info; /* quantity (+, −, the number and its frame, cart and product page): no corners on phones */ if (el?.closest?.('s-quantity-selector, button[command="--update-quantity"], [data-quantity-display]') || (info && (info.el.closest('s-quantity-selector') || info.el.matches('[data-quantity-display], button[command="--update-quantity"]') || info.el.querySelector?.('[data-quantity-display], s-quantity-selector')))) return null; /* cart product (photo or name): no corners, the name just turns green */ if (info && info.el.closest('.xo-cart-line') && (info.el.closest('a[href]') || info.el.matches('img') || el?.closest?.('.xo-cart-line a[href]'))) return null; /* search box + search chips: no corners on phones (chips just turn green) */ if (info && (info.el.matches('input[type="search"]') || info.el.querySelector('input[type="search"]') || info.el.closest('xo-search-history [data-xo-history], xo-search-history [data-xo-suggestions]'))) return null; /* logo: corners on tap, even though it is icon-sized */ return info && ((isIcon(info.el) && !info.el.closest('s-quantity-selector')) || (info.el.matches('svg') && !info.el.closest('s-quantity-selector')) || info.line || info.el.matches('[data-xo-tabs] > a') || info.el.closest('[data-clear-search], .xo-clear-all') || info.el.closest('s-product-gallery') || info.el.closest('.xo-vid') || info.el.closest('.xo-nav-products') || info.el.closest('form[action*="localization"], [id*="localization"]')) ? null : info; };
  const box = document.createElement('div');
  box.className = 'xo-touch-corners';
  box.setAttribute('aria-hidden', 'true');
  const mount = () => document.body.append(box);
  if (document.body) mount(); else document.addEventListener('DOMContentLoaded', mount);

  let target = null, line = false, raf = 0, px = 0, py = 0;
  // Smooth glide: the corners ease toward their target every frame (no CSS size transitions,
  // so they can never freeze half-way between two elements).
  let cur = null;
  const place = (r, jump) => {
    box.classList.toggle('is-small', !line && Math.min(r.width, r.height) < 30);
    const pad = line ? 0 : 5;
    const t = { x: r.left - pad, y: r.top - pad, w: r.width + pad * 2, h: r.height + (line ? 4 : pad * 2) };
    if (!cur || jump) cur = t;
    else {
      const k = 0.3;
      cur = { x: cur.x + (t.x - cur.x) * k, y: cur.y + (t.y - cur.y) * k, w: cur.w + (t.w - cur.w) * k, h: cur.h + (t.h - cur.h) * k };
      if (Math.abs(t.x - cur.x) + Math.abs(t.y - cur.y) + Math.abs(t.w - cur.w) + Math.abs(t.h - cur.h) < 1) cur = t;
    }
    box.style.transform = `translate(${cur.x}px, ${cur.y}px)`;
    box.style.width = `${cur.w}px`;
    box.style.height = `${cur.h}px`;
    if (window.xoHeaderClip) window.xoHeaderClip(box, null)
  };
  const follow = () => {
    if (target && !target.isConnected) {
      // options re-render after a choice: stay on the new element under the finger
      const info = resolve(document.elementFromPoint(px, py));
      target = info ? info.el : null;
      mark(target); // the re-rendered option is the one under the corners now
    }
    if (!target) { hide(); return; }
    place(target.getBoundingClientRect());
    raf = requestAnimationFrame(follow);
  };
  const mark = (el) => { document.querySelectorAll('[data-xo-snapped-touch]').forEach((n) => { if (n !== el) n.removeAttribute('data-xo-snapped-touch'); }); if (el) el.setAttribute('data-xo-snapped-touch', ''); };
  const hide = () => { target = null; cur = null; mark(null); cancelAnimationFrame(raf); box.classList.remove('is-on', 'is-line'); };
  // Phone keyboard opening/closing shifts the screen under fixed elements: drop the corners instead of leaving them stuck in the wrong spot.
  if (window.visualViewport) { let vh = window.visualViewport.height; window.visualViewport.addEventListener('resize', () => { const h = window.visualViewport.height; if (Math.abs(h - vh) > 80) hide(); vh = h; }); }
  const show = (info) => {
    if (info.el === target) return;
    cancelAnimationFrame(raf);
    const visible = box.classList.contains('is-on') && cur;
    box.classList.remove('is-line');
    target = info.el;
    mark(target);
    line = false;
    place(target.getBoundingClientRect(), !visible); // already showing: glide; hidden: appear in place
    box.classList.add('is-on');
    if (info.line) {
      // corners first, then they spread out and drop into a line under the word
      setTimeout(() => {
        if (target !== info.el) return;
        line = true;
        box.classList.add('is-line');
      }, 140);
    }
    raf = requestAnimationFrame(follow);
  };

  let sx = 0, sy = 0, moved = false, holdTimer = 0, liftTimer = 0, start = null;
  document.addEventListener('touchstart', (e) => {
    const t = e.touches[0];
    sx = px = t.clientX; sy = py = t.clientY; moved = false;
    start = resolve(e.target);
    clearTimeout(liftTimer);
    clearTimeout(holdTimer);
    holdTimer = setTimeout(() => { if (!moved) { if (start) show(start); else hide(); } }, 180);
  }, { passive: true });
  let scanPending = false;
  document.addEventListener('touchmove', (e) => {
    const t = e.touches[0];
    px = t.clientX; py = t.clientY;
    if (!moved && Math.hypot(t.clientX - sx, t.clientY - sy) > 10) {
      moved = true;
      clearTimeout(holdTimer);
    }
    // scrolling/swiping never moves or shows the corners: they appear only on a tap (or press)
  }, { passive: true });
  document.addEventListener('touchend', () => {
    clearTimeout(holdTimer);
    if (!moved) { if (start) show(start); else hide(); }
    // the corners show while pressed and fade out shortly after the finger lifts
    clearTimeout(liftTimer);
    liftTimer = setTimeout(hide, 450);
  }, { passive: true });
  window.addEventListener('pagehide', hide);
  // coming back with the Back button (page restored from memory) or switching apps: start clean
  window.addEventListener('pageshow', (e) => { if (e.persisted) hide(); });
  document.addEventListener('visibilitychange', () => { if (document.hidden) hide(); });
})();

// Touch: when a tapped link opens another page, wait a moment so the corners are seen first.
(() => {
  if (!window.matchMedia('(hover: none), (pointer: coarse)').matches) return;
  let lastTouch = 0;
  document.addEventListener('touchend', () => { lastTouch = Date.now(); }, { passive: true, capture: true });
  document.addEventListener('click', (e) => {
    if (Date.now() - lastTouch > 600 || e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
    if (!a || a.target === '_blank' || a.hasAttribute('download') || a.hasAttribute('data-load-more') || a.hasAttribute('commandfor')) return;
    const href = a.getAttribute('href') || '';
    if (!href || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
    if (!window.xoSnapTarget || !window.xoSnapTarget(e.target)) return;
    if (!a.hasAttribute('data-xo-logo') && a.querySelector('svg') && a.getBoundingClientRect().width <= 60) return; // icons: no corners, no wait (the logo does get corners)
    if (window.xoSnapTarget(e.target)?.line || a.matches('[data-xo-tabs] > a')) return; // text links & header tabs: colour only, no wait
    e.preventDefault();
    setTimeout(() => { window.location.href = a.href; }, 280);
  }, true);
})();

// Touch: corners stay visible above open dialogs (cart, menu, search) and buttons that open or
// close a panel (cart icon, menu, X) wait a moment so the corners are seen before the panel moves.
(() => {
  if (!window.matchMedia('(hover: none), (pointer: coarse)').matches) return;
  const getBox = () => document.querySelector('.xo-touch-corners');
  const canPopover = typeof HTMLElement.prototype.showPopover === 'function';
  const raise = () => {
    const box = getBox();
    if (!box || !canPopover) return;
    if (!box.hasAttribute('popover')) box.setAttribute('popover', 'manual');
    try { if (box.matches(':popover-open')) box.hidePopover(); box.showPopover(); } catch (e) {}
  };
  const start = () => {
    raise();
    new MutationObserver((records) => {
      if (records.some((r) => r.target instanceof HTMLDialogElement)) requestAnimationFrame(raise);
    }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setTimeout(start, 0));
  else setTimeout(start, 0);

})();

// Touch: icons turn brand green while pressed (and a moment after), instead of corners.
(() => {
  if (!window.matchMedia('(hover: none), (pointer: coarse)').matches) return;
  const textOf = (el) => {
    const tab = el instanceof Element ? el.closest('[data-xo-tabs] > a, form[action*="localization"] button, form[action*="localization"] a, form[action*="localization"] summary, [id*="localization"] button, [id*="localization"] a') : null;
    if (tab) return tab;
    const navProduct = el instanceof Element ? el.closest('.xo-nav-products a') : null;
    if (navProduct) return navProduct;
    return window.xoSnapTarget && window.xoSnapTarget(el)?.line ? window.xoSnapTarget(el).el : null;
  };
  const iconOf = (el) => {
    const n = el instanceof Element ? el.closest('a, button, [role="button"], summary') : null;
    if (!n || !n.querySelector('svg')) return null;
    const r = n.getBoundingClientRect();
    return r.width <= 60 && r.height <= 60 ? n : null;
  };
  document.addEventListener('touchstart', (e) => {
    const n = iconOf(e.target) || textOf(e.target);
    if (!n) return;
    n.classList.add('xo-icon-pressed');
    setTimeout(() => n.classList.remove('xo-icon-pressed'), 450);
  }, { passive: true });
})();

// Mouse: rows that scroll sideways (brand strip, chips, thumbnails) can be dragged with the mouse,
// and the mouse wheel scrolls them sideways while the pointer is over them.
(() => {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  const rowOf = (el) => {
    let n = el instanceof Element ? el : null;
    while (n && n !== document.body) {
      if (n.scrollWidth > n.clientWidth + 2) {
        const ox = getComputedStyle(n).overflowX;
        if (ox === 'auto' || ox === 'scroll') return n;
      }
      n = n.parentElement;
    }
    return null;
  };
  let row = null, startX = 0, startLeft = 0, dragged = false;
  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    if (e.target.closest?.('input, textarea, select')) return;
    row = rowOf(e.target);
    if (!row) return;
    startX = e.clientX; startLeft = row.scrollLeft; dragged = false;
  });
  document.addEventListener('pointermove', (e) => {
    if (!row) return;
    const dx = e.clientX - startX;
    if (!dragged && Math.abs(dx) > 5) { dragged = true; row.style.scrollBehavior = 'auto'; row.style.cursor = 'grabbing'; row.style.userSelect = 'none'; }
    if (dragged) row.scrollLeft = startLeft - dx;
  });
  const end = () => {
    if (!row) return;
    row.style.cursor = ''; row.style.userSelect = ''; row.style.scrollBehavior = '';
    setTimeout(() => { dragged = false; }, 0);
    row = null;
  };
  document.addEventListener('pointerup', end);
  document.addEventListener('pointercancel', end);
  // a drag is not a click: don't open the chip the mouse was released on
  document.addEventListener('click', (e) => { if (dragged) { e.preventDefault(); e.stopPropagation(); dragged = false; } }, true);
  document.addEventListener('dragstart', (e) => { if (rowOf(e.target)) e.preventDefault(); });
  document.addEventListener('wheel', (e) => {
    if (e.ctrlKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    const r = rowOf(e.target);
    if (!r || getComputedStyle(r).overflowY === 'auto') return;
    const max = r.scrollWidth - r.clientWidth;
    const rtl = getComputedStyle(r).direction === 'rtl';
    const next = r.scrollLeft + (rtl ? -e.deltaY : e.deltaY);
    // let the page scroll once the row reaches its end
    const atEnd = rtl ? (next > 0 || next < -max) : (next < 0 || next > max);
    if (atEnd) return;
    e.preventDefault();
    r.scrollLeft = next;
  }, { passive: false });
})();

// Side menu: only one collection dropdown open at a time (fallback for browsers without
// <details name>), and the first one is open whenever the menu opens with none open.
(() => {
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.matches('[data-xo-side-acc]') || !d.open) return;
    document.querySelectorAll('[data-xo-side-acc][open]').forEach((o) => { if (o !== d) o.open = false; });
  }, true);
  new MutationObserver((records) => {
    records.forEach((r) => {
      const dlg = r.target;
      if (dlg.id !== 'mobile-nav-drawer' || !dlg.open) return;
      const all = dlg.querySelectorAll('[data-xo-side-acc]');
      if (all.length && !dlg.querySelector('[data-xo-side-acc][open]')) all[0].open = true;
    });
  }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });
})();

// Side menu: when a collection is opened by the shopper, scroll the menu so that collection's
// title sits at the top of the panel (after the previously open one has closed).
(() => {
  document.addEventListener('click', (e) => {
    const sum = e.target instanceof Element ? e.target.closest('[data-xo-side-acc] > summary') : null;
    if (!sum) return;
    const d = sum.parentElement;
    if (d.open) return; // it is closing
    const scroller = d.closest('.overflow-y-auto');
    if (!scroller) return;
    const go = () => {
      const top = d.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
      scroller.scrollTo({ top: Math.max(0, top - 4), behavior: 'smooth' });
    };
    setTimeout(go, 60);
    setTimeout(go, 420); // settle after the other dropdown's close animation
  });
})();

// Filter panel: after its option list is refreshed, nudge one repaint so the content never stays invisible.
(() => {
  const nudge = (dialog) => {
    const inner = dialog.firstElementChild;
    if (!inner) return;
    inner.style.opacity = '0.999';
    requestAnimationFrame(() => requestAnimationFrame(() => { inner.style.opacity = ''; }));
  };
  const watch = () => {
    document.querySelectorAll('#collection-filter-drawer, #search-filter-drawer').forEach((dialog) => {
      new MutationObserver(() => { if (dialog.open) nudge(dialog); }).observe(dialog, { childList: true, subtree: true });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch); else watch();
})();

// Footer policies: shortest to longest by how wide the words really look on screen (not by letter
// count), on phones and desktop alike. Measured with the link's own font, so hidden lists sort too.
(() => {
  const ctx = document.createElement('canvas').getContext('2d');
  const sortList = (ul) => {
    const items = [...ul.children].filter((li) => li.querySelector('a'));
    const width = (li) => {
      const a = li.querySelector('a');
      const s = getComputedStyle(a);
      ctx.font = `${s.fontStyle} ${s.fontWeight} ${s.fontSize} ${s.fontFamily}`;
      return ctx.measureText(a.textContent.trim()).width;
    };
    items.map((li) => [li, width(li)]).sort((x, y) => x[1] - y[1]).forEach(([li]) => ul.append(li));
  };
  const run = () => document.querySelectorAll('[data-xo-sort-width]').forEach(sortList);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
  document.fonts?.ready.then(run);
})();

// Open panel (search, menu, cart, notifications, filters): freeze the page underneath on phones too.
// iPhone Safari ignores overflow:hidden on the page, so the page is pinned in place while a panel is
// open and put back exactly where it was when the panel closes.
(() => {
  let locked = false, savedY = 0;
  const anyOpen = () => !!document.querySelector('dialog.overlay-panel[open]');
  const lock = () => {
    if (locked) return;
    locked = true;
    savedY = window.scrollY;
    const b = document.body.style;
    b.position = 'fixed'; b.top = `-${savedY}px`; b.insetInline = '0'; b.width = '100%'; b.overflow = 'hidden';
  };
  const unlock = () => {
    if (!locked) return;
    locked = false;
    const b = document.body.style;
    b.position = ''; b.top = ''; b.insetInline = ''; b.width = ''; b.overflow = '';
    window.scrollTo({ top: savedY, behavior: 'instant' });
  };
  const sync = () => (anyOpen() ? lock() : unlock());
  const start = () => {
    new MutationObserver(sync).observe(document.body, { attributes: true, attributeFilter: ['open'], subtree: true });
    sync();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();

/* Header entrance always plays when the page is actually seen. After a language switch (or a page the browser
   prepared in the background / restored from history) the entrance could already be over before the page
   showed, so it looked like no animation. Replay it at the moment the page becomes visible. */
(() => {
  const replay = () => {
    const head = document.querySelector('.xo-header-grid.xo-a-open');
    if (!head) return;
    const list = [head, ...head.querySelectorAll('*')].flatMap((el) => el.getAnimations ? el.getAnimations() : [])
      .filter((a) => a.animationName && /^xo-/.test(a.animationName));
    list.forEach((a) => { a.cancel(); a.play(); });
  };
  const shownLate = performance.now() > 400;
  if (document.prerendering) document.addEventListener('prerenderingchange', replay, { once: true });
  else if (document.visibilityState === 'hidden') document.addEventListener('visibilitychange', replay, { once: true });
  else if (shownLate) requestAnimationFrame(replay);
  window.addEventListener('pageshow', (e) => { if (e.persisted) replay(); });
})();

// Cart (page and panel): a removed product slides out and folds away while the request runs (no waiting),
// and the total counts down/up to the new amount. The count keeps running even when the cart re-renders.
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const keyOf = (el) => (el.closest('#cart-drawer') ? 'drawer' : 'page');
  const totalFor = (key) => [...document.querySelectorAll('[data-cart-total]')].find((e) => keyOf(e) === key);
  const shown = new Map();   // key -> cents currently displayed (or being counted to)
  const runs = new Map();    // key -> { from, to, t0 }
  const fmt = (cents) => {
    const v = Math.max(0, Math.round(cents));
    const int = Math.floor(v / 100).toLocaleString('en-US');
    const dec = String(v % 100).padStart(2, '0');
    return `${int}<sup class="xo-money-dec">${dec}</sup>`;
  };
  const paint = (key, cents) => { const n = totalFor(key)?.querySelector('.xo-money-num'); if (n) n.innerHTML = fmt(cents); };
  const DUR = 400;
  const tick = () => {
    const now = performance.now();
    runs.forEach((r, key) => {
      const p = Math.min(1, (now - r.t0) / DUR);
      const e = 1 - Math.pow(1 - p, 3);
      paint(key, r.from + (r.to - r.from) * e);
      if (p >= 1) runs.delete(key);
    });
    if (runs.size) requestAnimationFrame(tick);
  };
  const countTo = (key, to) => {
    const from = shown.get(key);
    shown.set(key, to);
    if (from == null || from === to) return;
    if (reduce) { paint(key, to); return; }
    const r = runs.get(key);
    const cur = r ? r.from + (r.to - r.from) * (1 - Math.pow(1 - Math.min(1, (performance.now() - r.t0) / DUR), 3)) : from;
    const start = !runs.size;
    runs.set(key, { from: cur, to, t0: performance.now() });
    if (start) requestAnimationFrame(tick);
  };
  const seen = new WeakSet(); // only a freshly rendered total element carries news; our own painting must not re-trigger
  const sync = () => {
    document.querySelectorAll('[data-cart-total]').forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      const key = keyOf(el);
      const cents = Number(el.dataset.xoCents);
      if (Number.isNaN(cents)) return;
      if (!shown.has(key)) { shown.set(key, cents); return; }
      if (runs.has(key)) { if (runs.get(key).to !== cents) countTo(key, cents); return; }
      if (shown.get(key) !== cents) { const old = shown.get(key); paint(key, old); countTo(key, cents); }
    });
  };
  const start = () => {
    sync();
    new MutationObserver(() => sync()).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();

  // + / − : the total counts right away to the expected amount instead of waiting for the store's reply.
  document.addEventListener('click', (e) => {
    const step = e.target.closest?.('button[command="--update-quantity"]');
    if (!step) return;
    const row = step.closest('.xo-cart-line');
    const display = row?.querySelector('[data-quantity-display]');
    const unit = Number(row?.dataset.xoUnit);
    if (!display || Number.isNaN(unit)) return;
    const key = keyOf(row);
    const before = parseInt(display.value ?? display.textContent, 10) || 0;
    setTimeout(() => {
      const after = parseInt(display.value ?? display.textContent, 10) || 0;
      if (after !== before && shown.has(key)) countTo(key, shown.get(key) + (after - before) * unit);
    }, 0);
  }, true);

  document.addEventListener('click', (e) => {
    const btn = e.target.closest?.('button[command="--remove"]');
    if (!btn) return;
    const row = btn.closest('.xo-cart-line');
    if (!row || row.dataset.xoLeaving) return;
    row.dataset.xoLeaving = 'on';
    const key = keyOf(row);
    const line = Number(row.dataset.xoLine);
    if (!Number.isNaN(line) && shown.has(key)) countTo(key, shown.get(key) - line);
    if (reduce) return;
    const h = row.offsetHeight;
    const dir = document.documentElement.dir === 'rtl' ? 1 : -1;
    row.style.overflow = 'hidden';
    row.animate([
      { opacity: 1, transform: 'none', height: h + 'px' },
      { opacity: 0, transform: `translateX(${dir * 40}px)`, height: h + 'px', offset: 0.5 },
      { opacity: 0, transform: `translateX(${dir * 40}px)`, height: '0px', paddingTop: '0px', paddingBottom: '0px' }
    ], { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' });
  }, true);
})();

// Stock limit: trying to go past the available quantity pops a small note out of the quantity box
// (cart and product page). It floats above the box, so the page never changes size.
(() => {
  const frameOf = (node) => {
    const row = node.closest('.xo-cart-line');
    if (row) return row.querySelector('[data-quantity-display]')?.parentElement || null;
    return node.closest('s-quantity-selector');
  };
  const msgFor = (node) => {
    const row = node.closest('.xo-cart-line');
    if (row) return row.querySelector('[data-xo-stock-msg]');
    const qs = node.closest('s-quantity-selector');
    return qs?.querySelector('[data-xo-stock-msg]') || qs?.parentElement?.querySelector('[data-xo-stock-msg]') || null;
  };
  const show = (node) => {
    const msg = msgFor(node);
    const frame = frameOf(node);
    if (!msg || !frame) return;
    frame.classList.add('xo-qty-anchor');
    if (msg.parentElement !== frame) frame.appendChild(msg);
    msg.hidden = false;
    msg.classList.remove('is-in', 'is-out'); void msg.offsetWidth; msg.classList.add('is-in');
    clearTimeout(msg._t); clearTimeout(msg._t2);
    msg._t = setTimeout(() => { msg.classList.add('is-out'); msg._t2 = setTimeout(() => { msg.hidden = true; msg.classList.remove('is-in', 'is-out'); }, 250); }, 2500);
  };
  // cart: + at the limit does nothing, so show why
  document.addEventListener('click', (e) => {
    const inc = e.target.closest?.('button[command="--update-quantity"]:not([data-delta^="-"])');
    if (inc) {
      const row = inc.closest('.xo-cart-line');
      const input = row?.querySelector('[data-quantity-display]');
      const max = Number(input?.dataset.qtyMax);
      const before = parseInt(input?.value, 10) || 0;
      if (input && max && before >= max) show(inc);
      return;
    }
    // product page: the note appears when + reaches the limit
    const up = e.target.closest?.('s-quantity-selector [data-increment]');
    if (up) {
      const qs = up.closest('s-quantity-selector');
      setTimeout(() => {
        const max = Number(qs.getAttribute('max'));
        const v = parseInt(qs.querySelector('input')?.value, 10) || 0;
        if (max && v >= max) show(qs);
      }, 0);
    }
  }, true);
  // typing a bigger number than allowed (cart or product page)
  document.addEventListener('change', (e) => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'number') return;
    const max = Number(input.getAttribute('max') || input.dataset.qtyMax);
    if (max && (parseInt(input.value, 10) || 0) > max) show(input);
  }, true);
})();

// Videos inside product descriptions and page content: the browser's own controls are replaced by
// site controls (play, progress, time, sound, full screen), so the green mouse corners work on them.
(() => {
  const I = {
    play: '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>',
    pause: '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z"/></svg>',
    sound: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13"/></svg>',
    mute: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4z"/><path d="m22 9-6 6M16 9l6 6"/></svg>',
    full: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5"/></svg>',
    exit: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8h5V3M21 8h-5V3M3 16h5v5M21 16h-5v5"/></svg>',
  };
  const ar = (document.documentElement.lang || '').startsWith('ar');
  const L = ar ? { play: 'تشغيل', pause: 'إيقاف مؤقت', mute: 'كتم الصوت', unmute: 'تشغيل الصوت', full: 'ملء الشاشة', exit: 'الخروج من ملء الشاشة', seek: 'التقدم' }
               : { play: 'Play', pause: 'Pause', mute: 'Mute', unmute: 'Unmute', full: 'Full screen', exit: 'Exit full screen', seek: 'Progress' };
  const fmt = (s) => { s = Math.max(0, Math.floor(s || 0)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
  const fsEl = () => document.fullscreenElement || document.webkitFullscreenElement;

  const enhance = (video) => {
    if (video.closest('.xo-vid')) return;
    video.controls = false;
    video.removeAttribute('controls');
    video.playsInline = true;
    const wrap = document.createElement('div');
    wrap.className = 'xo-vid is-paused';
    video.parentNode.insertBefore(wrap, video);
    wrap.appendChild(video);
    wrap.insertAdjacentHTML('beforeend', `
      <button type="button" class="xo-vid-big" aria-label="${L.play}">${I.play}</button>
      <div class="xo-vid-bar" dir="ltr">
        <button type="button" class="xo-vid-btn" data-act="play" aria-label="${L.play}">${I.play}</button>
        <input type="range" class="xo-vid-seek" min="0" max="1000" step="1" value="0" aria-label="${L.seek}">
        <span class="xo-vid-time">0:00</span>
        <button type="button" class="xo-vid-btn" data-act="mute" aria-label="${L.mute}">${I.sound}</button>
        <button type="button" class="xo-vid-btn" data-act="full" aria-label="${L.full}">${I.full}</button>
      </div>`);
    const big = wrap.querySelector('.xo-vid-big');
    const playBtn = wrap.querySelector('[data-act="play"]');
    const muteBtn = wrap.querySelector('[data-act="mute"]');
    const fullBtn = wrap.querySelector('[data-act="full"]');
    const seek = wrap.querySelector('.xo-vid-seek');
    const time = wrap.querySelector('.xo-vid-time');
    let seeking = false;

    const toggle = () => { if (video.paused || video.ended) video.play().catch(() => {}); else video.pause(); };
    const syncPlay = () => {
      const p = video.paused || video.ended;
      wrap.classList.toggle('is-paused', p);
      playBtn.innerHTML = p ? I.play : I.pause;
      playBtn.setAttribute('aria-label', p ? L.play : L.pause);
    };
    const syncTime = () => {
      const d = video.duration || 0;
      if (!seeking) {
        seek.value = d ? Math.round((video.currentTime / d) * 1000) : 0;
        seek.style.setProperty('--p', `${seek.value / 10}%`);
      }
      time.textContent = d ? `${fmt(video.currentTime)} / ${fmt(d)}` : fmt(video.currentTime);
    };
    // Video with no sound: speaker shows closed and can't be switched on.
    let silent = false;
    const syncMute = () => {
      const off = silent || video.muted;
      muteBtn.innerHTML = off ? I.mute : I.sound;
      muteBtn.setAttribute('aria-label', off ? L.unmute : L.mute);
      muteBtn.disabled = silent;
      muteBtn.classList.toggle('is-silent', silent);
    };
    const checkAudio = () => {
      if (silent) return true;
      let none = null;
      if (typeof video.mozHasAudio === 'boolean') none = !video.mozHasAudio;
      else if (video.audioTracks && typeof video.audioTracks.length === 'number') none = video.audioTracks.length === 0;
      else if (typeof video.webkitAudioDecodedByteCount === 'number' && video.currentTime > 0.4) none = video.webkitAudioDecodedByteCount === 0;
      if (none === true) { silent = true; syncMute(); }
      return none !== null;
    };
    ['loadeddata', 'canplay'].forEach((ev) => video.addEventListener(ev, checkAudio));
    const onTime = () => { if (checkAudio()) video.removeEventListener('timeupdate', onTime); };
    video.addEventListener('timeupdate', onTime);
    // Chrome: decode a moment silently in the background to learn whether there is sound at all.
    video.preload = 'auto';
    const syncFull = () => {
      const on = fsEl() === wrap;
      wrap.classList.toggle('is-full', on);
      fullBtn.innerHTML = on ? I.exit : I.full;
      fullBtn.setAttribute('aria-label', on ? L.exit : L.full);
    };

    big.addEventListener('click', toggle);
    playBtn.addEventListener('click', toggle);
    video.addEventListener('click', toggle);
    muteBtn.addEventListener('click', () => { if (!silent) video.muted = !video.muted; });
    fullBtn.addEventListener('click', () => {
      if (fsEl()) (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
      else if (wrap.requestFullscreen) wrap.requestFullscreen().catch(() => {});
      else if (wrap.webkitRequestFullscreen) wrap.webkitRequestFullscreen();
      else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen(); // iPhone: only the video itself can go full screen
    });
    video.addEventListener('dblclick', () => fullBtn.click());
    // phones: a tap shows the bar for a moment
    let touchT;
    wrap.addEventListener('touchstart', () => { wrap.classList.add('is-touched'); clearTimeout(touchT); touchT = setTimeout(() => wrap.classList.remove('is-touched'), 2500); }, { passive: true });
    seek.addEventListener('input', () => {
      seeking = true;
      seek.style.setProperty('--p', `${seek.value / 10}%`);
      if (video.duration) video.currentTime = (seek.value / 1000) * video.duration;
    });
    seek.addEventListener('change', () => { seeking = false; });
    ['play', 'pause', 'ended'].forEach((ev) => video.addEventListener(ev, syncPlay));
    ['timeupdate', 'loadedmetadata', 'durationchange'].forEach((ev) => video.addEventListener(ev, syncTime));
    video.addEventListener('volumechange', syncMute);
    document.addEventListener('fullscreenchange', syncFull);
    document.addEventListener('webkitfullscreenchange', syncFull);
    syncPlay(); syncTime(); syncMute();
  };

  const scan = (root = document) => root.querySelectorAll?.('.richtext video').forEach(enhance);
  const start = () => {
    scan();
    new MutationObserver((recs) => { for (const r of recs) r.addedNodes.forEach((n) => { if (n.nodeType === 1) { if (n.matches?.('.richtext video')) enhance(n); else scan(n); } }); })
      .observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();

// Pull to refresh — only when the site is opened from the home-screen icon (there the phone has no
// refresh of its own). Pull down from the very top: a green arrow follows the finger; let go past the
// line and the page reloads.
(() => {
  const standalone = window.matchMedia('(display-mode: standalone)').matches || window.matchMedia('(display-mode: fullscreen)').matches || navigator.standalone === true;
  if (!standalone || !('ontouchstart' in window)) return;
  const LIMIT = 70, MAX = 110;
  const ind = document.createElement('div');
  ind.className = 'xo-ptr';
  ind.setAttribute('aria-hidden', 'true');
  ind.innerHTML = '<span class="xo-ptr-i"><svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 4v5h-5"/></svg></span>';
  const mount = () => document.body.append(ind);
  if (document.body) mount(); else document.addEventListener('DOMContentLoaded', mount);
  let startX = 0, startY = 0, pull = 0, active = false, decided = false, busy = false;
  const blocked = (t) => document.querySelector('dialog[open]') || t.closest?.('input, textarea, select, [contenteditable="true"], .xo-vid, s-product-gallery') || (window.scrollY || document.documentElement.scrollTop) > 0;
  const render = () => {
    ind.style.transform = `translate(-50%, ${pull - 56}px)`;
    const icon = ind.firstElementChild; if (icon) icon.style.transform = `rotate(${pull * 3}deg)`; // only the arrow turns; the square stays straight
    ind.style.opacity = String(Math.min(1, pull / 40));
    ind.classList.toggle('is-ready', pull >= LIMIT);
  };
  document.addEventListener('touchstart', (e) => {
    if (busy || e.touches.length !== 1 || blocked(e.target)) { active = false; return; }
    startX = e.touches[0].clientX; startY = e.touches[0].clientY;
    active = true; decided = false; pull = 0;
    ind.classList.remove('is-back');
  }, { passive: true });
  document.addEventListener('touchmove', (e) => {
    if (!active) return;
    const dx = e.touches[0].clientX - startX;
    const dy = e.touches[0].clientY - startY;
    if (!decided) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      decided = true;
      if (dy <= 0 || Math.abs(dx) > Math.abs(dy)) { active = false; return; } // scrolling up or sideways: not a pull
    }
    if ((window.scrollY || document.documentElement.scrollTop) > 0) { active = false; return; }
    if (e.cancelable) e.preventDefault();
    pull = Math.min(MAX, Math.max(0, dy * 0.5));
    render();
  }, { passive: false });
  const end = () => {
    if (!active) return;
    active = false;
    if (pull >= LIMIT) {
      busy = true;
      pull = LIMIT;
      ind.classList.add('is-back');
      ind.style.transform = `translate(-50%, ${LIMIT - 56}px)`;
      ind.classList.add('is-loading');
      const icon = ind.firstElementChild; if (icon) icon.style.transform = '';
      setTimeout(() => location.reload(), 700); // let the spin show before the page reloads
    } else {
      pull = 0;
      ind.classList.add('is-back');
      // slides back up while still visible, then fades at the top
      ind.classList.remove('is-ready');
      ind.style.transform = 'translate(-50%, -80px)';
      const icon = ind.firstElementChild; if (icon) icon.style.transform = 'rotate(0deg)';
      ind.style.opacity = '0';
    }
  };
  document.addEventListener('touchend', end, { passive: true });
  document.addEventListener('touchcancel', end, { passive: true });
  // leaving the app mid-pull (no touchend arrives): put the arrow away so it isn't left hanging
  const reset = () => {
    active = false; busy = false; pull = 0;
    ind.classList.remove('is-back', 'is-ready', 'is-loading');
    ind.style.transform = 'translate(-50%, -80px)'; ind.style.opacity = '0';
    const icon = ind.firstElementChild; if (icon) icon.style.transform = '';
  };
  document.addEventListener('visibilitychange', () => { if (document.hidden) reset(); });
  window.addEventListener('pagehide', reset);
  window.addEventListener('blur', () => { if (!busy) reset(); });
  window.addEventListener('pageshow', (e) => { if (e.persisted) reset(); });
})();

// Touch: tapping a product in the cart (its photo or its name) turns the name green for a moment.
(() => {
  if (!window.matchMedia('(hover: none), (pointer: coarse)').matches) return;
  document.addEventListener('touchstart', (e) => {
    const link = e.target instanceof Element ? e.target.closest('.xo-cart-line a[href]') : null;
    const row = link?.closest('.xo-cart-line');
    if (!row) return;
    row.classList.add('xo-line-tapped');
    clearTimeout(row._xoTap);
    row._xoTap = setTimeout(() => row.classList.remove('xo-line-tapped'), 450);
  }, { passive: true });
  // a swipe (scrolling the cart) is not a tap: put the colour back
  let sx = 0, sy = 0;
  document.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true, capture: true });
  document.addEventListener('touchmove', (e) => {
    const t = e.touches[0];
    if (Math.hypot(t.clientX - sx, t.clientY - sy) < 10) return;
    document.querySelectorAll('.xo-cart-line.xo-line-tapped').forEach((r) => r.classList.remove('xo-line-tapped'));
  }, { passive: true });
})();
